/**
 * WHAT THE BASKET COUNTS TOWARDS FREE DELIVERY — for the shop hero's chip, the
 * product page's free-shipping bar and its "₪X more for free delivery".
 *
 * "Free delivery over ₪200" is measured on the amount the BUSINESS chose
 * (owner decision, CHK-23 / DISC-32g): the basket AFTER its discounts by
 * default, or its shelf price when it chose "before discounts". The shop-page
 * payload names that choice (`freeDelivery.basis`); the checkout quote measures
 * on it server-side. These surfaces used to compare the threshold with the
 * cart's shelf total whatever the business chose, so during a 15% sale a ₪220
 * bracelet said "free delivery unlocked" over a ₪187 order the checkout then
 * charged delivery on.
 *
 * After discounts, the basket is priced the way the cart drawer prices it
 * (`useCartPricing`): the server's quote, with the same campaign rule standing
 * in while it is on its way — asked only while a threshold is on the page and
 * the basket has something in it. Before discounts, the cart's own shelf total,
 * with nothing asked.
 */
import useCartPricing from './useCartPricing';
import { freeDeliveryBasisOf, freeDeliverySpend } from '../utils/shopPromotions';

/**
 * Pure: the amount a threshold is compared with. `cart` is the cart state
 * (`items`, shelf `total`); `pricing` is `useCartPricing`'s answer for it.
 */
export function freeDeliveryCartSpend({ basis, cart, pricing }) {
  const shelf = Math.max(0, Number(cart?.total) || 0);
  if (freeDeliveryBasisOf(basis) === 'BEFORE_DISCOUNTS') return freeDeliverySpend({ subtotal: shelf }, 'BEFORE_DISCOUNTS');
  if (!pricing) return shelf;
  return freeDeliverySpend(
    { subtotal: Number(pricing.subtotal) || 0, discount: Number(pricing.discountTotal) || 0 },
    'AFTER_DISCOUNTS',
  );
}

export default function useFreeDeliverySpend(salonSlug, cart, { basis, threshold } = {}) {
  const items = cart?.items ?? [];
  const after = freeDeliveryBasisOf(basis) === 'AFTER_DISCOUNTS';
  const pricing = useCartPricing(salonSlug, items, {
    active: !!salonSlug && after && Number(threshold) > 0 && items.length > 0,
  });
  return freeDeliveryCartSpend({ basis, cart, pricing: after && items.length > 0 ? pricing : null });
}
